/*  ArbiScanSwapButton.js  */
import React, { useState, useCallback } from "react";
import { Button } from "primereact/button";
import BigNumber from "bignumber.js";
import { formatPercentage } from "../../utils";   // already present in Wallet.js
import { toast } from "react-toastify";
import routerAbi from "../../contracts/routerAbi";
import Web3 from "web3";
import HTTPProvider from "web3";

 const web3 = new Web3("https://binance.llamarpc.com/sk_llama_e2e9915b5438c5313ae3d634858d85cb");

const STATES = {
  IDLE:   "ArbiScan",
  BUSY:   "Scanning…",
  PROFIT: "Swap",       // label completed later with % gain
  NONE:   "No Profit Found",
  ERR:    "Error"
};

/**
 * Props
 * ─ token       → walletHoldings[i] object for the current card
 * ─ provider    → ethers/web3 provider (same one Wallet already has)
 * ─ getLiquidityPairs(token) → your existing helper
 */
export default function ArbiScanSwapButton({ token, provider, getLiquidityPairs }) {
  const [state, setState]         = useState(STATES.IDLE);
  const [bestPath, setBestPath]   = useState(null);   // array of addresses
  const [gainPct,  setGainPct]    = useState(0);

  /** ─────────────────────────────────────────────────────────
   *  ❶ 1-hop scan (Token A ➜ Token B ➜ Token A)
   *  --------------------------------------------------------- */
  const scan = useCallback(async () => {
    try {
      setState(STATES.BUSY);

      // 0. Guard rails
      if (!provider || !token?.balanceOfHolder || token.balanceOfHolder === "0") {
        throw new Error("Provider or balance missing");
      }

      /** 1. Collect first-hop pairs for the source token */
      const hop1 = await getLiquidityPairs(token);   // RocketFi/x
      if (!hop1.length) { setState(STATES.NONE); return; }

      /** 2. Iterate each hop-1 pair and look for a round-trip */
      let maxGain = new BigNumber(0);
      let winningPath = null;

      // routerContract + ABI reused over loop to save gas/time
      const { Contract, utils } = await import("ethers");
      const routerAddr = hop1[0].router;     // each oracle result already includes the dex router address
      console.log("Router address:", routerAddr);
      const router     = new web3.eth.Contract(routerAbi, routerAddr);

      const amountInRaw = token.balanceOfHolder;   // wei / raw units
      const bnAmountIn  = BigNumber(amountInRaw);
      console.log(`🚀 Starting scan for ${token.symbol} with balance: ${bnAmountIn.toFormat()}`);
      console.log(`🔍 Found ${hop1.length} first-hop pairs`);

      for (const p1 of hop1) {
        const interAddr = p1.backingToken;          // RocketFi/BTC → BTC
        /* 2a. pull second hop pairs for intermediary token (BTC/x) */
        const hop2 = await getLiquidityPairs({ ...token, address: interAddr });
        for (const p2 of hop2) {
          if (p2.backingToken.toLowerCase() !== token.address.toLowerCase())
            continue;

          const roundTripPath = [token.address, interAddr, token.address];

          try {
            const amounts = await router.methods
              .getAmountsOut(amountInRaw, roundTripPath)
              .call();

            const outRaw = new BigNumber(amounts[2]);
            const gain = outRaw.minus(bnAmountIn);
            const gainPct = gain.div(bnAmountIn).times(100);

            // 🔍 Log full path + results
            console.log(`🧪 Path tested: [${roundTripPath.join(" → ")}]`);
            console.log(`   ➤ Amount In:  ${bnAmountIn.toFixed()}`);
            console.log(`   ➤ Amount Out: ${outRaw.toFixed()}`);
            console.log(
              `   ➤ Gain:       ${gain.toFixed()} (${gainPct.toFixed(4)}%)`
            );

            if (gain.gt(0) && gainPct.gt(maxGain)) {
              maxGain = gainPct;
              winningPath = roundTripPath;
              console.log(
                `✅ Best path updated to [${roundTripPath.join(
                  " → "
                )}] with gain ${gainPct.toFixed(2)}%`
              );
            }
          } catch (err) {
            console.warn(
              `⚠️ Failed to fetch amounts for path: [${roundTripPath.join(
                " → "
              )}]`,
              err.message
            );
          }
        }

      }

      /** 3. Update UI state */
      if (winningPath) {
        setBestPath(winningPath);
        setGainPct(maxGain.toNumber());
        setState(STATES.PROFIT);
      } else {
        setState(STATES.NONE);
      }
    } catch (err) {
      console.error("ArbiScanSwap error:", err);
      toast.error(err.message || "ArbiScanSwap failed");
      setState(STATES.ERR);
    }
  }, [token, provider, getLiquidityPairs]);

  /** ❷ swap execution (placeholder – hook into your existing swap flow) */
  const swap = async () => {
    if (!bestPath) return;
    toast.info("Swap logic not wired yet – execute trade with bestPath here");
    // ...call router.swapExactTokensForTokens etc.
  };

  /** ❸ render  */
  return (
    <Button
      label={
        state === STATES.PROFIT
          ? `Swap (+${formatPercentage(gainPct/100)})`
          : state
      }
    //   className={
    //     state === STATES.PROFIT
    //       ? "p-button-success"
    //       : state === STATES.NONE
    //       ? "p-button-secondary"
    //       : "p-button-outlined"
    //   }
      loading={state === STATES.BUSY}
      disabled={state === STATES.BUSY || state === STATES.ERR}
      onClick={() => (state === STATES.PROFIT ? swap() : scan())}
      style={{ width: "100%", marginTop: "4px",
        backgroundColor: state === STATES.ERR ? "#f44336" : state === STATES.NONE ? "#9e9e9e" : state === STATES.BUSY ? "#2196f3" : "#4caf50",
        color: "#fff", borderColor: "transparent",
        // small button
        //height: "32px",
        //fontSize: "14px",
        alignSelf: "flex-end",
       }}
    />
  );
}

ArbiScanSwapButton.displayName = "ArbiScanSwapButton";   // for debugging